import React, { useRef, useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Slider,
  Typography,
  CircularProgress,
} from "@mui/material";
import { PhotoCamera, Close, Delete } from "@mui/icons-material";
import Cropper from "react-easy-crop";
import { useSnackbar } from "notistack";
import userService from "../../services/userService";
import { getCroppedImageBlob } from "../../lib/cropImage";

// Self-contained avatar + upload + crop dialog — pick a photo, crop it to
// a square, upload, done. `onUploaded`/`onRemoved` receive the fresh user
// object from the server response so the caller can push it into Redux
// (setCredentials) without this component needing to know about the store.
export default function ProfilePhotoUploader({ photoUrl, initials, size = 72, onUploaded, onRemoved }) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [rawImage, setRawImage] = useState(null); // object URL of the picked file, pre-crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // lets picking the same file again re-fire onChange
    if (!file) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRawImage(URL.createObjectURL(file));
  };

  const handleCancelCrop = () => {
    if (rawImage) URL.revokeObjectURL(rawImage);
    setRawImage(null);
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImageBlob(rawImage, croppedAreaPixels);
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });

      const { data: presigned } = await userService.getPhotoUploadUrl(file);
      const { key, url } = presigned.data;
      await fetch(url, { method: "PUT", body: file, headers: { "Content-Type": "image/jpeg" } });

      const { data } = await userService.setPhoto(key);
      onUploaded?.(data.data.user);
      enqueueSnackbar("Profile photo updated", { variant: "success" });
      handleCancelCrop();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to upload photo", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const { data } = await userService.removePhoto();
      onRemoved?.(data.data.user);
      enqueueSnackbar("Profile photo removed", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to remove photo", { variant: "error" });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <Avatar src={photoUrl} sx={{ width: size, height: size, bgcolor: "primary.main", fontWeight: 700, fontSize: size * 0.32 }}>
          {initials}
        </Avatar>
        <IconButton
          size="small"
          onClick={() => fileInputRef.current?.click()}
          sx={{
            position: "absolute",
            bottom: -4,
            right: -4,
            bgcolor: "primary.main",
            color: "#fff",
            width: 26,
            height: 26,
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <PhotoCamera sx={{ fontSize: 14 }} />
        </IconButton>
        {photoUrl && (
          <IconButton
            size="small"
            onClick={handleRemove}
            disabled={removing}
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              bgcolor: "grey.700",
              color: "#fff",
              width: 22,
              height: 22,
              "&:hover": { bgcolor: "grey.900" },
            }}
          >
            {removing ? <CircularProgress size={12} color="inherit" /> : <Delete sx={{ fontSize: 12 }} />}
          </IconButton>
        )}
      </Box>
      <input ref={fileInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />

      <Dialog open={!!rawImage} onClose={saving ? undefined : handleCancelCrop} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography fontWeight={700}>Crop Photo</Typography>
          <IconButton size="small" onClick={handleCancelCrop} disabled={saving}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: "relative", width: "100%", height: 320, bgcolor: "grey.900", borderRadius: 1 }}>
            {rawImage && (
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
              />
            )}
          </Box>
          <Box sx={{ px: 1, pt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Zoom
            </Typography>
            <Slider size="small" min={1} max={3} step={0.05} value={zoom} onChange={(_, v) => setZoom(v)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCancelCrop} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveCrop} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "Save Photo"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
